/**
 * Hook providing real Evolu data and wallet factory for StartupTokenSweep.
 *
 * Queries cashuMint and cashuToken tables from Evolu and creates a
 * cashu-ts wallet factory for token verification. Replaces the
 * placeholder data sources used during initial development.
 *
 * SECURITY: This hook queries token secrets (bearer instruments). The data
 * flows only to StartupTokenSweep's verification logic. Never log secrets.
 *
 * Tier: All tiers (core infrastructure).
 *
 * @module cashu/hooks/useStartupSweepDataSource
 */

import { useMemo, useRef } from 'react';
import { useEvolu, useQuery } from '@evolu/react';
import { Wallet } from '@cashu/cashu-ts';
import type { StartupSweepDataSource, SweepWalletFactory } from './useStartupTokenSweep';

/** Return type for the useStartupSweepDataSource hook. */
export interface UseStartupSweepDataSourceResult {
  /** Data source with mints and tokens from Evolu. */
  readonly dataSource: StartupSweepDataSource;
  /** Factory that creates cashu-ts wallets for token verification. */
  readonly walletFactory: SweepWalletFactory;
}

/**
 * Provide real Evolu data and a wallet factory for startup token sweep.
 *
 * Queries all active mints and all tokens from Evolu, maps them to the
 * StartupSweepDataSource shape, and provides a wallet factory that
 * creates cashu-ts Wallet instances for checkProofsStates calls.
 *
 * @returns Object with dataSource and walletFactory for StartupTokenSweep
 */
export function useStartupSweepDataSource(): UseStartupSweepDataSourceResult {
  const evolu = useEvolu();

  const activeMintQueryRef = useRef(
    evolu.createQuery((db) =>
      db.selectFrom('cashuMint').select(['id', 'url']).where('isActive', '=', 1),
    ),
  );
  const activeMintQuery = activeMintQueryRef.current;

  const allTokensQueryRef = useRef(
    evolu.createQuery((db) =>
      db
        .selectFrom('cashuToken')
        .select([
          'mintId',
          'amount',
          'secret',
          'c',
          'keysetId',
          'state',
          'lockedTo',
          'spentAt',
          'spentInTxId',
        ]),
    ),
  );
  const allTokensQuery = allTokensQueryRef.current;

  const mintRows = useQuery(activeMintQuery);
  const tokenRows = useQuery(allTokensQuery);

  const dataSource: StartupSweepDataSource = useMemo(
    () => ({
      mints: mintRows.map((row) => ({
        id: String(row.id),
        url: String(row.url),
      })),
      tokens: tokenRows.map((row) => ({
        mintId: String(row.mintId),
        amount: row.amount as number,
        secret: String(row.secret),
        c: String(row.c),
        keysetId: String(row.keysetId),
        state: String(row.state),
        lockedTo: row.lockedTo != null ? String(row.lockedTo) : null,
        spentAt: row.spentAt != null ? String(row.spentAt) : null,
        spentInTxId: row.spentInTxId != null ? String(row.spentInTxId) : null,
      })),
    }),
    [mintRows, tokenRows],
  );

  const walletFactory: SweepWalletFactory = useMemo(
    () => ({
      createWallet: (mintUrl: string) => {
        try {
          const wallet = new Wallet(mintUrl);
          return {
            checkProofsStates: async (
              proofs: Array<{ amount: number; secret: string; C: string; id: string }>,
            ): Promise<Array<{ Y: string; state: string }>> => {
              const result = await wallet.checkProofsStates(proofs);
              return result as unknown as Array<{ Y: string; state: string }>;
            },
          };
        } catch {
          return null;
        }
      },
    }),
    [],
  );

  return { dataSource, walletFactory };
}
